import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@pentabd/ui";

import {
  Comments,
  ErrorModal,
  SelectEmployeeModal,
  SuccessModal,
} from "@components";
import {
  BasicInfo,
  ResignationModal,
  ResignationsHeader,
  SupportingDocument,
} from "./components";

import { executeWorkflow, startWorkflow } from "@api/workflow";

import {
  ResignationFormDataType,
  resignationFormValidation,
} from "@validations/HRM/resignation/resignationFormValidation";

import { getAccess } from "@helpers/permissionController";
import { restructureDocumentsData } from "@utils/documentDataConvert";
import { duration } from "@utils/duration";
import { GetEmployeeDetails } from "@utils/getEmployeeDetails";

import { useResignation } from "@hooks/HRM/resignation/useResignation";
import { useUser } from "@hooks/useUser";

import { ROUTES } from "@constants/routes";
import { STATUS } from "@constants/status.hrm";
import { URLS } from "@constants/urls";
import { DECISION_TYPE } from "@constants/workflow-decision-type";

const { FORWARD } = DECISION_TYPE;
const { DRAFT, SUCCESS } = STATUS;
const {
  HRM: { RESIGNATION },
} = ROUTES;

const ResignationDetails = () => {
  const [modalTitle, setModalTitle] = useState<string>("");
  const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [showApprovalButton, setShowApprovalButton] = useState<boolean>(false);
  const [isFileUploadDisabled, setIsFileUploadDisabled] =
    useState<boolean>(true);
  const [showActionButton, setShowActionButton] = useState<boolean>(false);
  const [isNavigate, setIsNavigate] = useState<boolean>(false);
  const [isSelectEmployeeModalOpen, setIsSelectEmployeeModalOpen] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const { user } = useUser();
  const { resignation } = useResignation(id ? Number(id) : undefined);

  const { employeeMasterId, realm_roles: roles, positionId } = user || {};

  const isFinalState = resignation?.isFinalState || false;
  const resignationData = resignation?.resignationsDemandDetails || {};
  const { resignationsDate } = resignationData;

  const { joiningDate } = resignationData?.employee || {};

  const { employeeName, employeePosition, employeeImage } = GetEmployeeDetails(
    resignationData?.employee,
  );

  // resignation modal
  const openModal = (status: string) => {
    setIsCommentModalOpen(true);
    setModalTitle(status);
  };
  const closeModal = () => setIsCommentModalOpen(false);

  // error and success modal
  const closeErrorModal = () => setIsErrorModalOpen(false);
  const successAction = () =>
    isNavigate ? navigate(RESIGNATION) : setIsSuccessModalOpen(false);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
      setIsNavigate(false);
    };
    return reset();
  }, []);

  useEffect(() => {
    if (resignationsDate) methods.setValue("demo", "details");
    else methods.setValue("demo", "create");
  }, [resignationsDate]);

  const methods = useForm<ResignationFormDataType>({
    resolver: yupResolver(resignationFormValidation),
    values: {
      ...resignationData,
      ...resignationData?.employee,
      ...restructureDocumentsData(
        resignationData?.resignationsDetailsEmpDocuments,
      ),
      ...restructureDocumentsData(
        resignationData?.resignationsDetailsHrDocuments,
      ),
      servicePeriod: duration(joiningDate, resignationsDate),
    },
  });

  const onSubmit: SubmitHandler<ResignationFormDataType> = (data: any) => {
    setIsLoading(true);
    // workflow
    executeWorkflow(
      URLS.RESIGNATIONS,
      resignation.id,
      {
        processInstanceId: resignation.processInstanceId,
        decisionType: modalTitle,
        nextReviewerId:
          modalTitle === FORWARD ? data.nextReviewerId : employeeMasterId,
        comment: data.comment,
      },
      (e) => {
        if (e.status === SUCCESS) {
          closeModal();
          setIsSuccessModalOpen(true);
          setIsNavigate(true);
        } else setIsErrorModalOpen(true);
        setIsLoading(false);
      },
    );
  };

  // hr action
  const sendForApproval = (prop: any) => {
    setIsLoading(true);
    startWorkflow(
      URLS.RESIGNATIONS,
      resignation.id,
      prop.nextReviewerId,
      (e) => {
        if (e.status === SUCCESS) {
          setIsSuccessModalOpen(true);
          setIsNavigate(true);
        } else setIsErrorModalOpen(true);
        setIsLoading(false);
      },
    );
  };

  useEffect(() => {
    const canInitiateResignation = getAccess(roles, "RESIGNATION_INITIATE");
    const isResignationSubmitted = resignation?.status.toUpperCase() === DRAFT;

    setShowApprovalButton(canInitiateResignation && isResignationSubmitted);
    setIsFileUploadDisabled(
      !(canInitiateResignation && isResignationSubmitted),
    );

    const isNextReviewer =
      resignation?.nextReviewerId === Number(employeeMasterId);
    setShowActionButton(isNextReviewer);
  }, [resignation, roles, employeeMasterId]);

  return (
    <>
      {/* MODAL SECTION */}
      <>
        <SuccessModal isModalOpen={isSuccessModalOpen} action={successAction} />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
        <SelectEmployeeModal
          isOpen={isSelectEmployeeModalOpen}
          onClose={() => setIsSelectEmployeeModalOpen(false)}
          action={sendForApproval}
          registeredName="nextReviewerId"
          isLoading={isLoading}
        />
      </>
      {/* MODAL SECTION */}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <ResignationModal
            title={modalTitle}
            isModalOpen={isCommentModalOpen}
            closeModal={closeModal}
            employeeName={employeeName}
            isLoading={isLoading}
          />
          <ResignationsHeader
            data={{ employeeName, employeePosition, employeeImage }}
            openModal={openModal}
            showActionButton={showActionButton}
            isFinalState={isFinalState}
            positionId={positionId}
            isLoading={isLoading}
          />
          <div className="container p-10">
            <BasicInfo />
            <SupportingDocument
              data={resignation}
              isFileUploadDisabled={isFileUploadDisabled}
              setIsSuccessModalOpen={setIsSuccessModalOpen}
              setIsErrorModalOpen={setIsErrorModalOpen}
            />
            <Comments data={resignation?.resignationsDemandFlows} />
            {showApprovalButton && (
              <div className="col-12 d-flex justify-content-end mt-10">
                <Button
                  fill="fill"
                  size="sm"
                  type="primary"
                  onClick={() => setIsSelectEmployeeModalOpen(true)}
                  loading={isLoading}
                >
                  {t("RESIGNATION.FORM.SEND_BUTTON")}
                </Button>
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default ResignationDetails;
